import type { ComponentProps, ReactNode } from "react";
import { Fragment } from "react";
import type { Entries } from "type-fest";

import { useTranslation2 } from "~/shared/i18n";
import { cn } from "~/shared/ui";
import { Card, CardContent, CardHeader } from "~/shared/ui/card";
import { Dd, Dl, Dt } from "~/shared/ui/description-list";

type Props = Omit<ComponentProps<typeof Card>, "children"> & {
  avatar: ReactNode;
  fullName: ReactNode;
  ratingScale: ReactNode;
  reviewContent: ReviewContentProps["data"];
  createdAt: ReactNode;
};

function ReviewCardTemplate({
  avatar,
  fullName,
  ratingScale,
  reviewContent,
  createdAt,
  className,
  ...props
}: Props) {
  return (
    <Card className={cn("bg-background-secondary gap-4", className)} {...props}>
      <CardHeader className="flex items-center gap-4 font-bold">
        {avatar}
        <div className="flex min-w-0 flex-col gap-1">
          {fullName}
          {createdAt}
          {ratingScale}
        </div>
      </CardHeader>
      <CardContent className="text-foreground grow">
        <ReviewContent data={reviewContent} contentClassName="line-clamp-3" />
      </CardContent>
    </Card>
  );
}

type ReviewContentProps = Omit<ComponentProps<typeof Dl>, "children"> & {
  data: {
    pros: ReactNode;
    cons: ReactNode;
    text: ReactNode;
  };
  contentClassName?: ComponentProps<"dd">["className"] | undefined;
};

function ReviewContent({ data, className, contentClassName, ...props }: ReviewContentProps) {
  const {
    postproc: { tc },
  } = useTranslation2();

  return (
    <Dl {...props}>
      {(Object.entries(data) as Entries<typeof data>).map(([title, content]) => {
        return (
          <Fragment key={title}>
            <Dt>{tc(title.toLocaleUpperCase())}</Dt>
            <Dd className={contentClassName}>{content}</Dd>
          </Fragment>
        );
      })}
    </Dl>
  );
}

export { ReviewCardTemplate, ReviewContent };
